import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { motion } from "framer-motion";
import axios from "axios";
import Layout from "../components/Layout";
import EditableText from "../components/EditableText";
import { 
  Plus, Video, Calendar, Users, Clock, Play, ExternalLink
} from "lucide-react";

const VideoRoomsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewRoom, setShowNewRoom] = useState(false);
  const [newRoom, setNewRoom] = useState({
    name: "",
    description: "",
    hosted_by: "",
    scheduled_time: "",
    max_participants: 50
  });
  const [creating, setCreating] = useState(false);
  // RSVP state — map of room_id → true if current user has RSVP'd.
  const [rsvpMap, setRsvpMap] = useState({});
  const [rsvpBusy, setRsvpBusy] = useState(null);
  // Admin attendee viewer
  const [attendeeRoom, setAttendeeRoom] = useState(null);
  const [attendeeList, setAttendeeList] = useState([]);
  const [attendeeLoading, setAttendeeLoading] = useState(false);

  const isAdmin = !!user && (user.is_admin || user.role === "founder");

  useEffect(() => {
    fetchRooms();
  }, []);

  const fetchRooms = async () => {
    try {
      const response = await axios.get(`${API}/video/rooms`);
      setRooms(response.data);
      // Probe my-rsvp for each scheduled/live room (parallel).
      const probes = await Promise.allSettled(
        response.data
          .filter(r => r.status !== "ended")
          .map(r =>
            axios.get(`${API}/video/rooms/${r.room_id}/my-rsvp`).then(res => [r.room_id, res.data.rsvped])
          )
      );
      const map = {};
      probes.forEach(p => { if (p.status === "fulfilled") map[p.value[0]] = p.value[1]; });
      setRsvpMap(map);
    } catch (error) {
      toast.error("Failed to load video rooms");
    } finally {
      setLoading(false);
    }
  };

  const handleRsvp = async (roomId, currentlyRsvped) => {
    setRsvpBusy(roomId);
    try {
      if (currentlyRsvped) {
        await axios.delete(`${API}/video/rooms/${roomId}/rsvp`);
        setRsvpMap(prev => ({ ...prev, [roomId]: false }));
        toast.success("RSVP cancelled");
      } else {
        await axios.post(`${API}/video/rooms/${roomId}/rsvp`, {});
        setRsvpMap(prev => ({ ...prev, [roomId]: true }));
        toast.success("You're in — check your email for confirmation");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "RSVP failed");
    } finally {
      setRsvpBusy(null);
    }
  };

  const openAttendees = async (room) => {
    setAttendeeRoom(room);
    setAttendeeLoading(true);
    try {
      const r = await axios.get(`${API}/video/rooms/${room.room_id}/rsvps`);
      setAttendeeList(r.data.rsvps || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load attendees");
    } finally {
      setAttendeeLoading(false);
    }
  };

  const handleCreateRoom = async (e) => {
    e.preventDefault();
    if (!newRoom.name.trim() || !newRoom.scheduled_time || !newRoom.hosted_by.trim()) {
      toast.error("Name, hosted by, and scheduled time are required");
      return;
    }

    setCreating(true);
    try {
      await axios.post(`${API}/video/rooms`, {
        ...newRoom,
        scheduled_time: new Date(newRoom.scheduled_time).toISOString()
      });
      toast.success("Video session created!");
      setShowNewRoom(false);
      setNewRoom({ name: "", description: "", hosted_by: "", scheduled_time: "", max_participants: 50 });
      fetchRooms();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create video session");
    } finally {
      setCreating(false);
    }
  };

  const handleJoinRoom = (roomId) => {
    navigate(`/video/${roomId}`);
  };

  const formatDateTime = (dateStr) => {
    return new Date(dateStr).toLocaleString("en-CA", {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "live": return "bg-[#10B981] text-white";
      case "ended": return "bg-[#64748B] text-white";
      default: return "bg-[#8DB5A0] text-[#6B4E8A]";
    }
  };

  const scheduledRooms = rooms.filter(r => r.status === "scheduled");
  const liveRooms = rooms.filter(r => r.status === "live");
  const pastRooms = rooms.filter(r => r.status === "ended");

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12" data-testid="video-rooms-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <EditableText
              blockKey="video.title"
              fallback="Video Sessions"
              as="h1"
              className="text-2xl sm:text-3xl font-medium text-[#6B4E8A]"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            />
            <EditableText
              blockKey="video.subtitle"
              fallback="Continuing education, courses, and seminars"
              as="p"
              className="text-[#64748B] mt-1"
              multiline
            />
          </div>
          <Dialog open={showNewRoom} onOpenChange={setShowNewRoom}>
            <DialogTrigger asChild>
              <Button className="bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full" data-testid="schedule-session-btn">
                <Plus className="w-4 h-4 mr-2" />
                Schedule Session
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-xl font-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Schedule a Video Session
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateRoom} className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="name">Session Name</Label>
                  <Input
                    id="name"
                    value={newRoom.name}
                    onChange={(e) => setNewRoom({ ...newRoom, name: e.target.value })}
                    placeholder="e.g., Infection Control Best Practices"
                    className="mt-2 bg-white border-[#E5DFEC] focus:border-[#6B4E8A]"
                    data-testid="session-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={newRoom.description}
                    onChange={(e) => setNewRoom({ ...newRoom, description: e.target.value })}
                    placeholder="What will this session cover?"
                    className="mt-2 bg-white border-[#E5DFEC] focus:border-[#6B4E8A]"
                    data-testid="session-description-input"
                  />
                </div>
                <div>
                  <Label htmlFor="hosted_by">Hosted By <span className="text-[#EF4444]">*</span></Label>
                  <Input
                    id="hosted_by"
                    value={newRoom.hosted_by}
                    onChange={(e) => setNewRoom({ ...newRoom, hosted_by: e.target.value })}
                    placeholder="e.g., Dr. Jane Smith, RDH"
                    className="mt-2 bg-white border-[#E5DFEC] focus:border-[#6B4E8A]"
                    data-testid="session-hosted-by-input"
                  />
                </div>
                <div>
                  <Label htmlFor="scheduled_time">Date & Time</Label>
                  <Input
                    id="scheduled_time"
                    type="datetime-local"
                    value={newRoom.scheduled_time}
                    onChange={(e) => setNewRoom({ ...newRoom, scheduled_time: e.target.value })}
                    className="mt-2 bg-white border-[#E5DFEC] focus:border-[#6B4E8A]"
                    data-testid="session-datetime-input"
                  />
                </div>
                <div>
                  <Label htmlFor="max_participants">Max Participants</Label>
                  <Input
                    id="max_participants"
                    type="number"
                    min="2"
                    max="100"
                    value={newRoom.max_participants}
                    onChange={(e) => setNewRoom({ ...newRoom, max_participants: parseInt(e.target.value) })}
                    className="mt-2 bg-white border-[#E5DFEC] focus:border-[#6B4E8A]"
                    data-testid="session-participants-input"
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowNewRoom(false)}
                    className="rounded-full"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full"
                    disabled={creating}
                    data-testid="submit-session-btn"
                  >
                    {creating ? "Creating..." : "Schedule Session"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Live Sessions */}
        {liveRooms.length > 0 && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <h2 className="text-lg font-medium text-[#6B4E8A] mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <span className="w-2 h-2 bg-[#10B981] rounded-full animate-pulse"></span>
              Live Now
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {liveRooms.map((room) => (
                <div
                  key={room.room_id}
                  className="bg-gradient-to-r from-[#10B981] to-[#059669] rounded-2xl p-6 text-white"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                      <Video className="w-6 h-6" />
                    </div>
                    <span className="px-3 py-1 bg-white/20 rounded-full text-sm">Live</span>
                  </div>
                  <h3 className="text-xl font-medium mb-2">{room.name}</h3>
                  <p className="text-white/80 text-sm mb-4 line-clamp-2">{room.description}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-white/80">
                      <Users className="w-4 h-4" />
                      Hosted by {room.host_name}
                    </div>
                    <Button
                      onClick={() => handleJoinRoom(room.room_id)}
                      className="bg-white text-[#10B981] hover:bg-white/90 rounded-full"
                      data-testid={`join-live-${room.room_id}`}
                    >
                      <Play className="w-4 h-4 mr-2" />
                      Join Now
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Scheduled Sessions */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <h2 className="text-lg font-medium text-[#6B4E8A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Upcoming Sessions
          </h2>
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white border border-[#E5DFEC] rounded-2xl p-6 animate-pulse">
                  <div className="h-12 w-12 bg-[#F0ECF7] rounded-xl mb-4"></div>
                  <div className="h-6 bg-[#F0ECF7] rounded w-3/4 mb-2"></div>
                  <div className="h-4 bg-[#F0ECF7] rounded w-full"></div>
                </div>
              ))}
            </div>
          ) : scheduledRooms.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {scheduledRooms.map((room, idx) => (
                <motion.div
                  key={room.room_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="bg-white border border-[#E5DFEC] rounded-2xl p-6 card-hover"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-[#F0ECF7] flex items-center justify-center">
                      <Video className="w-6 h-6 text-[#8DB5A0]" />
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(room.status)}`}>
                      Scheduled
                    </span>
                  </div>
                  <h3 className="text-lg font-medium text-[#6B4E8A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {room.name}
                  </h3>
                  <p className="text-sm text-[#64748B] line-clamp-2 mb-4">{room.description}</p>
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-sm text-[#64748B]">
                      <Calendar className="w-4 h-4" />
                      {formatDateTime(room.scheduled_time)}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-[#64748B]">
                      <Users className="w-4 h-4" />
                      Hosted by {room.hosted_by || room.host_name}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Button
                      onClick={() => handleJoinRoom(room.room_id)}
                      className="w-full bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full"
                      data-testid={`view-session-${room.room_id}`}
                    >
                      View Details
                    </Button>
                    <Button
                      onClick={() => handleRsvp(room.room_id, rsvpMap[room.room_id])}
                      disabled={rsvpBusy === room.room_id}
                      variant={rsvpMap[room.room_id] ? "outline" : "default"}
                      className={`w-full rounded-full ${
                        rsvpMap[room.room_id]
                          ? "border-[#10B981] text-[#10B981] hover:bg-[#ECFDF5]"
                          : "bg-[#8DB5A0] text-white hover:bg-[#7AA38C]"
                      }`}
                      data-testid={`rsvp-btn-${room.room_id}`}
                    >
                      {rsvpBusy === room.room_id
                        ? "..."
                        : rsvpMap[room.room_id] ? "✓ You're RSVP'd · Cancel" : "RSVP"}
                    </Button>
                    {isAdmin && (
                      <div className="flex gap-2">
                        <Button
                          onClick={() => openAttendees(room)}
                          variant="outline"
                          size="sm"
                          className="flex-1 text-xs rounded-full border-[#E5DFEC] text-[#6B4E8A]"
                          data-testid={`view-rsvps-${room.room_id}`}
                        >
                          <Users className="w-3 h-3 mr-1" /> Attendees
                        </Button>
                        <Button
                          onClick={async () => {
                            if (!window.confirm(`Permanently delete "${room.name}" and all its RSVPs?`)) return;
                            try {
                              await axios.delete(`${API}/video/rooms/${room.room_id}`);
                              toast.success("Session deleted");
                              fetchRooms();
                            } catch (e) {
                              toast.error(e.response?.data?.detail || "Delete failed");
                            }
                          }}
                          variant="outline"
                          size="sm"
                          className="text-xs rounded-full border-[#FCA5A5] text-[#EF4444] hover:bg-[#FEE2E2]"
                          data-testid={`delete-session-${room.room_id}`}
                        >
                          Delete
                        </Button>
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-white border border-[#E5DFEC] rounded-2xl">
              <Video className="w-12 h-12 mx-auto mb-4 text-[#E5DFEC]" />
              <h3 className="text-lg font-medium text-[#6B4E8A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                No upcoming sessions
              </h3>
              <p className="text-[#64748B] mb-4">Be the first to schedule a continuing education session!</p>
              <Button
                onClick={() => setShowNewRoom(true)}
                className="bg-[#8DB5A0] text-white hover:bg-[#D0694E] rounded-full"
              >
                <Plus className="w-4 h-4 mr-2" />
                Schedule Session
              </Button>
            </div>
          )}
        </motion.div>

        {/* Past Sessions */}
        {pastRooms.length > 0 && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h2 className="text-lg font-medium text-[#6B4E8A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Past Sessions
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {pastRooms.slice(0, 6).map((room) => (
                <div
                  key={room.room_id}
                  className="bg-white border border-[#E5DFEC] rounded-2xl p-6 opacity-70"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-10 h-10 rounded-xl bg-[#F0ECF7] flex items-center justify-center">
                      <Video className="w-5 h-5 text-[#64748B]" />
                    </div>
                    <span className="px-2 py-0.5 bg-[#F0ECF7] text-[#64748B] rounded-full text-xs">
                      Ended
                    </span>
                  </div>
                  <h3 className="font-medium text-[#6B4E8A] mb-1">{room.name}</h3>
                  <p className="text-xs text-[#64748B]">{formatDateTime(room.scheduled_time)}</p>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </div>

      {/* Admin: Attendee list modal */}
      <Dialog open={!!attendeeRoom} onOpenChange={(o) => !o && setAttendeeRoom(null)}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto" data-testid="video-attendees-modal">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit, sans-serif" }}>
              {attendeeRoom?.name} — RSVPs ({attendeeList.length})
            </DialogTitle>
          </DialogHeader>
          {attendeeLoading ? (
            <p className="text-sm text-[#94A3B8] py-6 text-center">Loading…</p>
          ) : attendeeList.length === 0 ? (
            <p className="text-sm text-[#94A3B8] py-6 text-center">No RSVPs yet.</p>
          ) : (
            <ul className="space-y-2 mt-2">
              {attendeeList.map((a) => (
                <li
                  key={a.rsvp_id}
                  className="flex items-center justify-between gap-3 px-3 py-2 rounded-xl border border-[#E5DFEC] bg-white"
                  data-testid={`attendee-row-${a.user_id}`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-[#6B4E8A] truncate">{a.name}</p>
                    <p className="text-xs text-[#64748B] truncate">{a.email}</p>
                  </div>
                  <span className="text-[10px] text-[#94A3B8] shrink-0">
                    {new Date(a.rsvped_at).toLocaleDateString("en-CA", { month: "short", day: "numeric" })}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default VideoRoomsPage;
